import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  User, Mail, Building2, IdCard, KeyRound, Loader2, CheckCircle2, AlertTriangle, Send,
} from "lucide-react";

function Field({ label, icon: Icon, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.14em] text-zinc-500 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-[11px] text-zinc-400 mt-1">{hint}</p>}
    </label>
  );
}

export default function SonographerAccount() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [busyName, setBusyName] = useState(false);
  const [busyPw, setBusyPw] = useState(false);
  const [busyVerify, setBusyVerify] = useState(false);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [nameMsg, setNameMsg] = useState(null);
  const [pwMsg, setPwMsg] = useState(null);
  const [verifyMsg, setVerifyMsg] = useState(null);
  const [devToken, setDevToken] = useState(null);

  const load = async () => {
    const { data } = await api.get("/users/me/profile");
    setProfile(data);
    setName(data.name || "");
    setUsername(data.username || "");
  };
  useEffect(() => { load(); }, []);

  const saveName = async (e) => {
    e.preventDefault();
    setNameMsg(null);
    if (!name.trim()) return setNameMsg({ ok: false, text: "Name cannot be empty." });
    const uChanged = username !== (profile.username || "");
    if (uChanged && username && !/^[A-Za-z0-9._-]{3,30}$/.test(username)) {
      return setNameMsg({ ok: false, text: "Username must be 3–30 chars using letters, numbers, '.', '_' or '-'." });
    }
    setBusyName(true);
    try {
      const payload = { name: name.trim() };
      if (uChanged && username) payload.username = username;
      const { data } = await api.patch("/users/me/profile", payload);
      setProfile(data);
      setUsername(data.username || "");
      setNameMsg({ ok: true, text: uChanged ? "Profile updated." : "Name updated." });
      refresh?.();
    } catch (e2) {
      setNameMsg({ ok: false, text: e2?.response?.data?.detail || "Could not update profile." });
    } finally { setBusyName(false); }
  };

  const savePw = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (!cur || !pw) return setPwMsg({ ok: false, text: "Fill in every field." });
    if (pw.length < 6) return setPwMsg({ ok: false, text: "New password must be at least 6 characters." });
    if (pw !== pw2) return setPwMsg({ ok: false, text: "New passwords don't match." });
    setBusyPw(true);
    try {
      await api.post("/users/me/password", { current_password: cur, new_password: pw });
      setPwMsg({ ok: true, text: "Password changed successfully." });
      setCur(""); setPw(""); setPw2("");
    } catch (e2) {
      setPwMsg({ ok: false, text: e2?.response?.data?.detail || "Could not change password." });
    } finally { setBusyPw(false); }
  };

  const resendVerify = async () => {
    setBusyVerify(true); setVerifyMsg(null); setDevToken(null);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data?.already_verified) {
        setVerifyMsg({ ok: true, text: "Your email is already verified." });
        await load();
      } else {
        setVerifyMsg({ ok: true, text: "Verification link generated." });
        if (data?.dev_verification_token) {
          setDevToken(data.dev_verification_token);
        }
      }
    } catch (e2) {
      setVerifyMsg({ ok: false, text: e2?.response?.data?.detail || "Could not send verification." });
    } finally { setBusyVerify(false); }
  };

  if (!profile) {
    return (
      <div className="card-elev flex items-center gap-2 text-sm text-zinc-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your account…
      </div>
    );
  }

  const verifyLink = devToken ? `/verify-email?token=${devToken}` : null;

  return (
    <div className="max-w-2xl">
      <span className="pill">Account</span>
      <h1 className="mt-2 text-3xl font-bold">Your account</h1>
      <p className="text-zinc-500 mt-1 text-sm">
        Personal details linked to your Sonote profile. Reach out to your clinic manager to change anything read-only.
      </p>

      {/* Email verification banner */}
      {!profile.email_verified && (
        <div className="card-elev mt-6 border-amber-300 bg-amber-50" data-testid="account-verify-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-amber-900">Verify your email address</div>
              <p className="text-sm text-amber-800 mt-1">
                We sent a verification link to <span className="font-semibold">{profile.email}</span>.
                Verifying secures your account and unlocks clinic invitations.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  onClick={resendVerify}
                  disabled={busyVerify}
                  className="btn-outline h-9 text-sm inline-flex items-center gap-2"
                  data-testid="account-resend-verify"
                >
                  {busyVerify ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Resend verification email
                </button>
                {verifyMsg && (
                  <span className={`text-xs font-semibold ${verifyMsg.ok ? "text-emerald-700" : "text-red-600"}`}>
                    {verifyMsg.text}
                  </span>
                )}
              </div>
              {verifyLink && (
                <div className="mt-3 rounded-md bg-white border border-amber-200 px-3 py-2 text-xs" data-testid="account-dev-verify-link">
                  <span className="font-bold text-amber-800">Dev mode:</span>{" "}
                  <a href={verifyLink} className="text-amber-800 underline break-all">{verifyLink}</a>
                  <div className="text-[10px] text-amber-700 mt-1">
                    (No email provider is wired yet, so we surface the link here so you can test the flow.)
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {profile.email_verified && (
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1 text-xs font-semibold" data-testid="account-verified-badge">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Email verified
        </div>
      )}

      {/* Profile card */}
      <div className="card-elev mt-6 space-y-5" data-testid="account-profile-card">
        <div className="flex items-center gap-2">
          <User className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
          <h2 className="font-bold text-lg">Profile</h2>
        </div>

        <form onSubmit={saveName} className="space-y-4">
          <Field label="Name" icon={User}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-11 rounded-md border border-zinc-200 px-3 focus:ring-2 focus:ring-[#d4af37] outline-none"
              data-testid="account-name-input"
              maxLength={120}
            />
          </Field>

          <Field label="Username" icon={User} hint="3–30 characters · letters, numbers, '.', '_' or '-' · you use this to sign in.">
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full h-11 rounded-md border border-zinc-200 px-3 focus:ring-2 focus:ring-[#d4af37] outline-none font-mono tracking-wide"
              data-testid="account-username-input"
              maxLength={30}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder={profile.username ? "" : "your_username"}
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email" icon={Mail} hint="Contact your clinic manager to change your email.">
              <input
                type="email"
                value={profile.email}
                readOnly
                className="w-full h-11 rounded-md border border-zinc-200 px-3 bg-zinc-50 text-zinc-500"
                data-testid="account-email-input"
              />
            </Field>
            <Field label="Clinic" icon={Building2} hint="Sonographers can't self-transfer. Reach out to your manager.">
              <input
                type="text"
                value={profile.clinic_name || "Not linked to a clinic"}
                readOnly
                className="w-full h-11 rounded-md border border-zinc-200 px-3 bg-zinc-50 text-zinc-500"
                data-testid="account-clinic-input"
              />
            </Field>
            <Field label="Sonography Canada number" icon={IdCard} hint="Read-only — updated by your clinic manager during onboarding.">
              <input
                type="text"
                value={profile.sonography_canada_number || "—"}
                readOnly
                className="w-full h-11 rounded-md border border-zinc-200 px-3 bg-zinc-50 text-zinc-500 font-mono tracking-wide"
                data-testid="account-sca-input"
              />
            </Field>
            <Field label="Role" icon={User}>
              <input
                type="text"
                value={profile.role?.replace(/_/g, " ")}
                readOnly
                className="w-full h-11 rounded-md border border-zinc-200 px-3 bg-zinc-50 text-zinc-500 capitalize"
                data-testid="account-role-input"
              />
            </Field>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            {nameMsg && (
              <span className={`text-sm font-semibold ${nameMsg.ok ? "text-emerald-700" : "text-red-600"}`} data-testid="account-name-msg">
                {nameMsg.text}
              </span>
            )}
            <button
              type="submit"
              disabled={busyName || (name === profile.name && username === (profile.username || ""))}
              className="btn-gold h-10 px-5 text-sm ml-auto"
              data-testid="account-save-name"
            >
              {busyName ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save profile
            </button>
          </div>
        </form>
      </div>

      {/* Change password */}
      <div className="card-elev mt-6" data-testid="account-password-card">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
          <h2 className="font-bold text-lg">Change password</h2>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Enter your current password, then choose a new one. Use at least 6 characters — a passphrase is best.
        </p>
        <form onSubmit={savePw} className="grid sm:grid-cols-2 gap-4">
          <Field label="Current password">
            <input
              type="password" autoComplete="current-password" value={cur}
              onChange={(e) => setCur(e.target.value)}
              className="w-full h-11 rounded-md border border-zinc-200 px-3 focus:ring-2 focus:ring-[#d4af37] outline-none"
              data-testid="account-current-pw"
            />
          </Field>
          <div className="hidden sm:block" />
          <Field label="New password">
            <input
              type="password" autoComplete="new-password" value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="w-full h-11 rounded-md border border-zinc-200 px-3 focus:ring-2 focus:ring-[#d4af37] outline-none"
              data-testid="account-new-pw"
            />
          </Field>
          <Field label="Confirm new password">
            <input
              type="password" autoComplete="new-password" value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="w-full h-11 rounded-md border border-zinc-200 px-3 focus:ring-2 focus:ring-[#d4af37] outline-none"
              data-testid="account-new-pw2"
            />
          </Field>
          <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3 pt-1">
            {pwMsg && (
              <span className={`text-sm font-semibold ${pwMsg.ok ? "text-emerald-700" : "text-red-600"}`} data-testid="account-pw-msg">
                {pwMsg.text}
              </span>
            )}
            <button
              type="submit"
              disabled={busyPw}
              className="btn-gold h-10 px-5 text-sm ml-auto"
              data-testid="account-save-pw"
            >
              {busyPw ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update password
            </button>
          </div>
        </form>
      </div>

      {/* Small footer */}
      <p className="text-xs text-zinc-400 mt-6">
        Signed in as {user?.email}
      </p>
    </div>
  );
}
